import { NamedEntityDto } from "approot/global/models/common.model";
import { LoginRepository } from "./login.repository";
import { LoginData, LoginDataService } from "./services/login.data.service";
import { LoginOptions } from "./services/login.service";
import { LoginStrategiesService } from "./services/login.strategies.service";
import { PasswordRecoveryService } from "./services/password.recovery.service";
import { AccountInfo, SessionStore, UserRoleDto } from "./sessionstore";
import { ChangeLanguageService } from "./services/changeLanguage.service";
import { DialogsService } from "approot/global/providers/uikit.dialogs";

export class AccountLoginController {
	private loginOptions: LoginOptions;
	private sessionStore: SessionStore;
	private mobile: boolean;
	private mobileAccessToken: string;
	private esiaMainAuth: boolean;

	public account: AccountInfo;
	public firstLetter: string;
	public organizations: NamedEntityDto[];
	public userRoles: UserRoleDto[];

	public showOrganizations: boolean = false;
	public showRoles: boolean = false;

	public activeOrg: NamedEntityDto;
	public activeRole: UserRoleDto;

	public loginName: string;
	public password: string;
	public remember: boolean = true;

	// видимый / невидимый пароль
	public showPassword: boolean = false;
	// режим редактирования логина
	public editLoginName: boolean = false;
	// режим редактирования пароля
	public editPassword: boolean = false;

	public ready: boolean;

	/*@ngInject*/
	constructor(
		private loginDataService: LoginDataService,
		private loginStrategiesService: LoginStrategiesService,
		private passwordRecoveryService: PasswordRecoveryService,
		private loginRepository: LoginRepository,
		private $location: ng.ILocationService,
		private $dialogs: DialogsService,
		public language: any
	) {
		this.init();
	}

	private init() {
		let loginData: LoginData = this.loginDataService.loginData;

		this.loginOptions = loginData.loginOptions;
		this.account = loginData.accounts?.find(x => (<any>x).logsIn);
		this.sessionStore = new SessionStore(this.loginRepository);
		this.esiaMainAuth = loginData.viewData.esiaMainAuth;

		let searchParams = new URLSearchParams(window.location.search);
		this.mobile = searchParams.has('mobile');
		if (this.mobile) {
			this.mobileAccessToken = searchParams.get('access_token');
		}

		if (this.account) {
			this.onReady();
		} else {
			this.goToAccounts();
		}
	}

	private onReady(): void {
		let sessionInfo: AccountInfo = this.account;
		let needInteract: boolean = sessionInfo.organizations?.length > 1 || sessionInfo.userRoles?.length > 1;
		this.organizations = sessionInfo.organizations;
		this.activeOrg = this.organizations.find(() => true);

		// немедленный вход
		if (this.allowLoginInActiveSession()) {
			this.loginStrategiesService.loginInActiveSession(sessionInfo);
		}
		else if (this.allowLoginWithRefreshToken() && !needInteract) {
			this.loginWithStoredRefreshToken(sessionInfo);
		}
		else {
			this.showLoginForm();
		}
	}

	private showLoginForm(): void {
		this.firstLetter = this.account.user.name?.charAt(0).toUpperCase();
		this.initUserRoles();
		this.ready = true;
	}

	toggleOrganizations() {
		this.showOrganizations = !this.showOrganizations;
	}

	initUserRoles() {
		this.userRoles = this.account.userRoles?.filter(x => x.schoolId == this.activeOrg.id);
		this.activeRole = this.userRoles?.find(() => true);
	}

	chooseOrganization(organization: NamedEntityDto) {
		this.activeOrg = organization;
		this.initUserRoles();
	}

	toggleRoles() {
		this.showRoles = !this.showRoles;
	}

	chooseRole(userRole: UserRoleDto) {
		this.activeRole = userRole;
	}

	togglePasswordIcon() {
		this.showPassword = !this.showPassword;
	}

	allowLoginInActiveSession(): boolean {
		return this.account.active && this.loginOptions.responseType != "code" && this.account.currentOrganization.id == this.activeOrg.id && !this.mobile;
	}

	allowLoginWithRefreshToken(): boolean {
		let getToken = this.sessionStore.getRefreshToken(this.account.user.id, this.activeOrg.id);
		return getToken?.refreshToken ? true: false;
	}

	allowLoginWithLoginPassCheck(): boolean {
		return !this.allowLoginWithRefreshToken();
	}

	allowForgetPassword(): boolean {
		return !this.esiaMainAuth;
	}

	private collectParams() {
		let params: any = {};

		if (this.mobile) {
			params.mobile = "1";
		}

		// не вложенный if, так как в инициализации вложен
		if (this.mobileAccessToken) {
			params.access_token = this.mobileAccessToken;
		}

		let queryLanguage = ChangeLanguageService.getQueryLanguage();
		if (queryLanguage != null) {
			params.lang = queryLanguage;
		}

		return params;
	}

	goToAccounts() {
		this.$location.path("/authorize/accounts").search(this.collectParams());
	}

	login() {
		let sessionInfo: AccountInfo = this.account;
		// дополнительные опции: - учет роли при входе
		let addOpts = (this.userRoles.length > 1 || this.loginOptions.acrValues) && this.activeRole
			? { rolegroup: this.activeRole.role.id }
			: {};

		if (this.allowLoginWithRefreshToken()) {
			this.loginWithStoredRefreshToken(sessionInfo, addOpts);
		}
		else {
			let schoolId: number = this.activeOrg.id;
			let options: LoginOptions = Object.assign({}, this.loginOptions, { saveAccount: this.remember });

			this.loginStrategiesService.loginWithLoginPassCheck(this.loginName, this.password, schoolId, null, options, addOpts);
		}
	}

	private loginWithStoredRefreshToken(sessionInfo: AccountInfo, addOpts?: Record<string, string | boolean | number | undefined>): void {
		const refreshToken = this.sessionStore.getRefreshToken(sessionInfo.user.id, this.activeOrg.id);
		if (!refreshToken) {
			this.showLoginForm();
			return;
		}

		this.loginStrategiesService.loginWithRefreshToken(refreshToken, addOpts)
			.catch(() => {
				this.sessionStore.removeRefreshToken(sessionInfo.user.id, this.activeOrg.id);
				this.showLoginForm();
				this.$dialogs.message('Не удалось продлить сеанс. Выполните вход по логину и паролю.');
			});
	}

	enter() {
		if (this.allowLoginWithLoginPassCheck()) {
			this.login();
		}
	}

	recoveryPassword() {
		this.passwordRecoveryService.goToPassRecovery();
	}
}

export const AccountLoginComponent = {
	templateUrl: "/static/dist/app/login/account.login.component.html",
	controllerAs: "$ctrl",
	controller: AccountLoginController
}
